/**
 * O shader do orbe da VecchIA (WebGL 1, GLSL ES 1.00, para rodar em qualquer celular).
 *
 * Um triângulo cobre o canvas quadrado; o fragment shader compõe, de trás para a frente e em
 * alfa pré-multiplicado:
 *
 * - **Enquadramento.** A esfera tem diâmetro de ~58% do lado menor, centrada. Halo e aura somem
 *   (alfa 0) antes da borda do canvas: nada é cortado.
 * - **Halo.** Brilho menta suave em volta da esfera e um anel luminoso na borda dela; `u_halo`
 *   diz o quanto (discreto em repouso, forte ouvindo e falando).
 * - **Aura.** Três pétalas de vidro menta translúcido fora da esfera, cada uma com preenchimento
 *   que brilha perto do contorno, contorno fino e três ecos por dentro. O raio de cada pétala varia
 *   com ruído amostrado na direção do pixel: `u_abertura` (graves) abre as ondas grandes, o tempo
 *   (que corre mais rápido com os médios) as faz fluir, `u_ondulacao` (agudos) dá o tremor fino do
 *   contorno. `u_aura` é a presença — quase zero em repouso; `u_recolher` (pensando) traz as
 *   pétalas para junto da esfera.
 * - **Esfera de vidro com tinta.** Ruído 3D sobre a esfera, girando mais no miolo, com domain
 *   warping de poucas oitavas: duas ou três volutas grandes e desfocadas de verde floresta e
 *   profundo dentro de vidro menta. A borda brilha em menta claro, a luz interna vem de baixo,
 *   um reflexo off-white cintila com os agudos. Pensando, o fluido corre para o centro e a luz
 *   interna se concentra.
 *
 * Custo: ~8 avaliações de simplex por pixel dentro da esfera, ~5 na região da aura e 2 no resto.
 */

export const VERTICE = `
attribute vec2 a_pos;
void main() {
    gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

export const FRAGMENTO = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2 u_tela;
uniform float u_tempo;
uniform float u_relogio;
uniform float u_deformacao;
uniform float u_fluxo;
uniform float u_ondulacao;
uniform float u_energia;
uniform float u_brilho;
uniform float u_recolher;
uniform float u_aura;
uniform float u_abertura;
uniform float u_halo;

const vec3 MENTA = vec3(0.1451, 0.9725, 0.6745);
const vec3 MENTA_CLARO = vec3(0.3569, 1.0, 0.8157);
const vec3 OFF_WHITE = vec3(0.9569, 0.9686, 0.9608);
const vec3 FLORESTA = vec3(0.0275, 0.2157, 0.1843);
const vec3 PROFUNDO = vec3(0.0157, 0.1059, 0.0980);
const vec3 GRAFITE = vec3(0.0667, 0.1020, 0.0941);

/* Raio da esfera: 1.0 é metade do lado menor, então o diâmetro é 58% do lado. */
const float RAIO = 0.58;

/* Simplex 3D — webgl-noise, Ashima Arts e Stefan Gustavson (licença MIT). */
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
        i.z + vec4(0.0, i1.z, i2.z, 1.0))
        + i.y + vec4(0.0, i1.y, i2.y, 1.0))
        + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    vec4 m = max(0.5 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
    m = m * m;
    return 105.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

/* Duas oitavas só: volutas grandes e macias, sem a textura fina que parecia mármore. */
float fbm2(vec3 p) {
    return 0.62 * snoise(p) + 0.26 * snoise(p * 2.03 + vec3(1.7, -2.3, 0.9));
}

vec2 girar(vec2 v, float a) {
    float c = cos(a);
    float s = sin(a);
    return vec2(c * v.x - s * v.y, s * v.x + c * v.y);
}

/* Compõe em alfa pré-multiplicado: frente sobre o que já estava atrás. */
vec4 sobre(vec4 frente, vec4 atras) {
    return frente + atras * (1.0 - frente.a);
}

float linhaFina(float e, float centro, float l2) {
    float x = e - centro;
    return exp(-(x * x) / l2);
}

/* Uma pétala da aura: vidro menta translúcido que brilha perto do contorno, brilho suave para
   fora, contorno fino e claro e três ecos por dentro. */
vec4 petala(float d, float borda, float forca, float pixel) {
    float e = borda - d;
    if (e < -0.06 || forca <= 0.001) return vec4(0.0);
    float largura = pixel * 0.9 + 0.0016;
    float l2 = largura * largura;
    float dentro = smoothstep(-pixel, pixel, e);
    float preenche = dentro * (0.25 + 0.75 * exp(-max(e, 0.0) * 6.0)) * 0.17;
    float brilhoFora = (1.0 - dentro) * exp(max(-e, 0.0) * -45.0) * 0.12;
    float contorno = linhaFina(e, 0.0, l2);
    float ecos = dentro * (linhaFina(e, 0.024, l2) * 0.5 + linhaFina(e, 0.05, l2) * 0.32 + linhaFina(e, 0.08, l2) * 0.18);
    float alfa = clamp((preenche + brilhoFora + contorno * 0.5 + ecos * 0.38) * forca, 0.0, 1.0);
    vec3 corDaLinha = mix(MENTA, MENTA_CLARO, 0.45);
    vec3 corDoVidro = mix(MENTA_CLARO, OFF_WHITE, 0.35);
    vec3 cor = mix(corDoVidro, corDaLinha, clamp(contorno + ecos, 0.0, 1.0));
    return vec4(cor * alfa, alfa);
}

void main() {
    float lado = min(u_tela.x, u_tela.y);
    vec2 p = (gl_FragCoord.xy - 0.5 * u_tela) / (0.5 * lado);
    float pixel = 2.0 / lado;
    float d = length(p);
    vec2 direcao = p / max(d, 0.0001);

    /* Superfície da esfera: respiração no relógio real, massas amplas (graves), ondulação fina (agudos). */
    float respira = 0.008 * sin(u_relogio * 0.9) + 0.004 * sin(u_relogio * 0.37 + 1.3);
    float massa = snoise(vec3(direcao * 0.7, u_tempo * 0.16));
    float fina = snoise(vec3(direcao * 4.2, u_tempo * 1.2));
    float raio = RAIO * (1.0 + respira + massa * u_deformacao + fina * 0.006 * u_ondulacao);

    /* Halo e aura somem antes da borda do canvas. */
    float limite = 1.0 - smoothstep(0.84, 0.98, d);

    /* Halo menta e anel luminoso na borda da esfera. */
    float fora = max(d - raio, 0.0);
    float espalha = 0.19 + 0.07 * u_energia;
    float halo = u_halo * exp(-(fora * fora) / (espalha * espalha)) * 0.5;
    float anel = u_halo * exp(-(fora * fora) / 0.0016) * 0.5;
    float alfaDoHalo = clamp(halo + anel, 0.0, 1.0) * limite;
    vec4 acumulado = vec4(mix(MENTA, MENTA_CLARO, 0.55) * alfaDoHalo, alfaDoHalo);

    /* Aura: três pétalas, da maior (atrás) para a menor (na frente). */
    if (u_aura > 0.004 && d > raio * 0.96 && d < 0.98) {
        vec2 dirA = girar(direcao, u_tempo * 0.045);
        vec2 dirB = girar(direcao, 1.7 - u_tempo * 0.035);
        float n1 = snoise(vec3(dirA * 0.8, u_tempo * 0.21));
        float n2 = snoise(vec3(dirB * 1.0 + vec2(3.1, -1.7), u_tempo * 0.29));
        float n3 = snoise(vec3(direcao * 6.0, u_tempo * 1.5));
        /* Picos mais agudos: pétalas, não bolhas. */
        float p1 = sign(n1) * pow(abs(n1), 0.7);
        float p2 = sign(n2) * pow(abs(n2), 0.7);
        float perto = 1.0 - 0.7 * u_recolher;
        float abre = (0.1 + 0.42 * u_abertura) * u_aura * perto;
        float tremor = u_ondulacao * 0.012 * u_aura * n3;
        float forca = smoothstep(0.0, 0.35, u_aura) * limite;
        float r3 = raio * (1.0 + u_aura * perto * 0.36 + abre * (0.6 * p1 + 0.4 * p2)) + tremor;
        float r1 = raio * (1.0 + u_aura * perto * 0.24 + abre * (0.75 * p2 - 0.35 * p1)) + tremor * 0.8;
        float r2 = raio * (1.0 + u_aura * perto * 0.13 + abre * (0.55 * p1 - 0.45 * p2)) + tremor * 0.6;
        acumulado = sobre(petala(d, min(r3, 0.93), forca * 0.85, pixel), acumulado);
        acumulado = sobre(petala(d, min(r1, 0.93), forca, pixel), acumulado);
        acumulado = sobre(petala(d, min(r2, 0.93), forca, pixel), acumulado);
    }

    /* Esfera de vidro menta com tinta escura girando dentro. */
    float mascara = 1.0 - smoothstep(raio - pixel * 1.3, raio + pixel * 0.7, d);
    if (mascara > 0.0) {
        vec2 q = p / raio;
        float qq = min(dot(q, q), 1.0);
        float z = sqrt(1.0 - qq);
        float r = sqrt(qq);
        vec3 normal = normalize(vec3(q + direcao * fina * 0.03 * u_ondulacao, z));

        float giro = u_tempo * 0.05 + (1.0 - r) * (1.1 + 0.9 * u_recolher);
        vec2 s = girar(q, giro);
        vec3 fp = vec3(s * 0.62, z * 0.35 + u_tempo * 0.07 + u_recolher * r * 0.7);
        vec2 torcao = vec2(fbm2(fp), fbm2(fp + vec3(5.2, 1.3, 2.8)));
        vec3 fp2 = fp + vec3(torcao * (0.9 + u_fluxo * 0.7 + u_deformacao * 6.0), u_tempo * 0.03);
        float f = fbm2(fp2 * 0.75 + vec3(1.7, 9.2, 0.0));
        float t = smoothstep(-0.5, 0.5, f * 1.15 + 0.18 * torcao.y);

        /* Rampa contínua: profundo → floresta → menta → menta claro, sem faixas. */
        vec3 cor = mix(PROFUNDO, FLORESTA, smoothstep(0.0, 0.32, t));
        cor = mix(cor, MENTA, smoothstep(0.26, 0.72, t));
        cor = mix(cor, MENTA_CLARO, smoothstep(0.66, 1.0, t));

        /* A tinta fica funda: a borda do vidro brilha em menta claro e a luz interna vem de baixo. */
        float borda = pow(1.0 - z, 1.3);
        cor = mix(cor, MENTA_CLARO, borda * 0.8);
        vec2 focoDaLuz = q - vec2(0.12, -0.42);
        float interna = exp(-dot(focoDaLuz, focoDaLuz) * mix(2.4, 5.0, u_recolher)) * (0.3 + 0.2 * u_recolher + 0.25 * u_energia);
        cor = mix(cor, MENTA_CLARO, clamp(interna, 0.0, 1.0));
        float centro = exp(-qq * 4.5) * (0.06 + 0.2 * u_recolher);
        cor = mix(cor, MENTA_CLARO, centro);

        vec3 luz = normalize(vec3(-0.45, 0.65, 0.62));
        float difusa = max(dot(normal, luz), 0.0);
        float refletido = max(reflect(-luz, normal).z, 0.0);
        float especular = pow(refletido, 16.0) * 0.28 + pow(refletido, 80.0) * 0.5;
        float cintila = 1.0 + u_ondulacao * 0.7 * fina;
        float fio = pow(1.0 - z, 9.0);
        cor *= 0.94 + 0.1 * difusa;
        float baseEscura = (1.0 - smoothstep(-1.0, -0.5, q.y)) * pow(1.0 - z, 3.0);
        cor = mix(cor, GRAFITE, baseEscura * 0.04);
        cor = mix(cor, OFF_WHITE, clamp(especular * cintila + fio * 0.45, 0.0, 1.0));
        cor = min(cor * u_brilho, vec3(1.0));

        float alfa = mascara * 0.98;
        acumulado = sobre(vec4(cor * alfa, alfa), acumulado);
    }

    gl_FragColor = acumulado;
}
`;
